import dayjs from 'dayjs';
import type { FtueFilter, FtuePlatform, FtueQuery, FtueVersion } from '@/types';

/** First install day the dashboard has data for (IST, 'YYYY-MM-DD'). */
export const MIN_DATE = '2026-10-17';
export const DAY_FORMAT = 'YYYY-MM-DD';

export type CompareMode = 'none' | 'version' | 'dates' | 'platform';

export interface FtueFilterState {
  primary: FtueFilter;
  compareMode: CompareMode;
  /** Only the part the mode uses is read; choosing a mode seeds that part (see the suggest functions). */
  compare: FtueFilter;
}

/**
 * The query for a filter state. The primary filter is always sent; of the compare filter only
 * the keys the mode uses, and none whose value equals the primary one (a view compared with
 * itself is not a comparison). Compare dates are sent as a pair or not at all.
 */
export function toFtueQuery({ primary, compareMode, compare }: FtueFilterState): FtueQuery {
  const query: FtueQuery = {
    from: primary.from,
    to: primary.to,
    version: primary.version,
    platform: primary.platform,
  };
  if (compareMode === 'version' && compare.version !== primary.version) {
    query.compareVersion = compare.version;
  } else if (
    compareMode === 'dates' &&
    (compare.from !== primary.from || compare.to !== primary.to)
  ) {
    query.compareFrom = compare.from;
    query.compareTo = compare.to;
  } else if (compareMode === 'platform' && compare.platform !== primary.platform) {
    query.comparePlatform = compare.platform;
  }
  return query;
}

/** True when the query asks for a compare view. */
export const hasCompare = (q: FtueQuery): boolean =>
  Boolean(q.compareVersion || q.compareFrom || q.compareTo || q.comparePlatform);

/** Today's date in IST ('YYYY-MM-DD'), whatever the browser's time zone. */
export function istToday(): string {
  const parts = new Intl.DateTimeFormat('en-US', {
    timeZone: 'Asia/Kolkata',
    year: 'numeric',
    month: '2-digit',
    day: '2-digit',
  }).formatToParts(new Date());
  const part = (type: string) => parts.find((p) => p.type === type)?.value ?? '';
  return `${part('year')}-${part('month')}-${part('day')}`;
}

const shiftDay = (date: string, days: number) => dayjs(date).add(days, 'day').format(DAY_FORMAT);

/**
 * A compare window that differs from the primary one: the same-length window just before it;
 * failing that (the primary starts on the first day of data) the one just after, up to today;
 * failing that the first day of data. Null when that is the primary window too (the data so
 * far is that single day). Dates are ISO strings, so they compare as text.
 */
export function suggestDates(primary: FtueFilter): { from: string; to: string } | null {
  const today = istToday();
  const length = dayjs(primary.to).diff(dayjs(primary.from), 'day') + 1;
  const beforeTo = shiftDay(primary.from, -1);
  if (beforeTo >= MIN_DATE) {
    const beforeFrom = shiftDay(beforeTo, -(length - 1));
    return { from: beforeFrom < MIN_DATE ? MIN_DATE : beforeFrom, to: beforeTo };
  }
  const afterFrom = shiftDay(primary.to, 1);
  if (afterFrom <= today) {
    const afterTo = shiftDay(afterFrom, length - 1);
    return { from: afterFrom, to: afterTo > today ? today : afterTo };
  }
  const first = { from: MIN_DATE, to: MIN_DATE };
  return first.from !== primary.from || first.to !== primary.to ? first : null;
}

/** The most-installed version other than the primary one; a different choice if there is none. */
export function suggestVersion(primary: FtueFilter, versions: FtueVersion[]): string {
  const other = versions.find((v) => v.version !== primary.version);
  if (other) return other.version;
  return primary.version === 'all' ? 'unknown' : 'all';
}

/** Android against iOS; from All, Android. */
export const otherPlatform = (p: FtuePlatform): FtuePlatform =>
  p === 'android' ? 'ios' : 'android';
